import api from './api';
import { cache } from '../utils/cache';

export interface Member {
  id: string;
  company_id: string;
  email: string;
  full_name: string;
  role: 'admin' | 'manager' | 'operator' | 'viewer';
  status: 'active' | 'inactive' | 'pending';
  is_active: boolean;
  last_login?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface Role {
  id: string;
  name: string;
  display_name: string;
  is_system: boolean;
  company_id?: string | null;
  description?: string | null;
  created_at: string;
  updated_at: string;
  member_count?: number;
  permissions_count?: number;
}

export interface InviteMemberRequest {
  email: string;
  full_name: string;
  role: string;
}

export interface AdminMember {
  id: string;
  email: string;
  full_name: string;
  company_name: string;
}

export const memberService = {
  getMembers: async () => {
    return cache.fetch('members_all', async () => {
      const response = await api.get<Member[]>('/members');
      return response.data;
    });
  },

  inviteMember: async (data: InviteMemberRequest) => {
    const response = await api.post<{ user: Member; password: string }>('/members/invite', data);
    cache.delete('members_all');
    cache.delete('members_admin');
    return response.data;
  },

  resetPassword: async (id: string) => {
    const response = await api.post<{ message: string; password: string }>(`/members/${id}/reset-password`);
    return response.data;
  },

  removeMember: async (id: string) => {
    const response = await api.delete(`/members/${id}`);
    cache.delete('members_all');
    cache.delete('members_admin');
    return response.data;
  },

  reactivateMember: async (id: string) => {
    const response = await api.post(`/members/${id}/reactivate`);
    cache.delete('members_all');
    cache.delete('members_admin');
    return response.data;
  },

  updateRole: async (id: string, role: string) => {
    const response = await api.patch(`/members/${id}/role`, { role });
    cache.delete('members_all');
    cache.delete('members_admin');
    return response.data;
  },

  getAdminMembers: async () => {
    return cache.fetch('members_admin', async () => {
      const response = await api.get<AdminMember[]>('/admin-members');
      return response.data;
    });
  },
  
  getRoles: async (): Promise<Role[]> => {
    return cache.fetch('roles_all', async () => {
      const response = await api.get<{ data: Role[] }>('/roles');
      return response.data.data;
    });
  },
};
